import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Avatar, Callout, Checkbox, Paragraph, Pill, Subheading } from '@treams/treams-ui';

import { ConversationRevealSummaryRow } from './ConversationRevealSummaryRow';
import { formatRevealValue } from './formatRevealValue';
import { RevealUploadSummary } from './useConversationRevealUpload';

interface IProps {
	summary: RevealUploadSummary;
	selectedFields: string[];
	fieldTypes: Record<string, graphql.RevealFieldType>;
	excludedUserIDs: string[];
	onToggleUser: (userID: string) => void;
}

export function ConversationRevealUploadSummary({
	summary,
	selectedFields,
	fieldTypes,
	excludedUserIDs,
	onToggleUser,
}: IProps) {
	const { t, i18n } = useTranslation();

	const { skippedCount, matchedUsers, skippedKeys } = summary;

	const matched = matchedUsers
		.map((entry) => ({
			...entry,
			selectedValues: entry.values.filter(
				(field) => selectedFields.includes(field.label) && field.value !== '',
			),
		}))
		.filter((entry) => entry.selectedValues.length > 0);
	const checkedCount = matched.filter((entry) => !excludedUserIDs.includes(entry.user.id)).length;
	const hasNoMatches = checkedCount === 0;

	return (
		<Box>
			<Callout color="blue" mb={3}>
				{t('conversations.reveal.upload.onlyNextConversation')}
			</Callout>

			{hasNoMatches && (
				<Callout color="yellow" mb={3}>
					{t('conversations.reveal.upload.noMatches')}
				</Callout>
			)}

			<Subheading mb={2}>{t('conversations.reveal.upload.summaryTitle')}</Subheading>

			<ConversationRevealSummaryRow
				title={t('conversations.reveal.upload.matched', { count: checkedCount })}
				expandable={matched.length > 0}
			>
				{matched.map((entry) => (
					<Flex key={entry.user.id} alignItems="center" mb={2} style={{ gap: 8 }}>
						<Checkbox
							checked={!excludedUserIDs.includes(entry.user.id)}
							onClick={() => onToggleUser(entry.user.id)}
						/>
						<Avatar user={entry.user} size={24} />
						<Paragraph>{entry.user.fullName}</Paragraph>
						<Flex flexWrap="wrap" style={{ gap: 4 }}>
							{entry.selectedValues.map((field) => (
								<Pill key={field.label} small>
									{field.label}:{' '}
									{formatRevealValue(
										{ ...field, type: fieldTypes[field.label] ?? field.type },
										i18n.language,
									)}
								</Pill>
							))}
						</Flex>
					</Flex>
				))}
			</ConversationRevealSummaryRow>

			<ConversationRevealSummaryRow
				title={t('conversations.reveal.upload.skipped', { count: skippedCount })}
				expandable={skippedKeys.length > 0}
			>
				{skippedKeys.map((key) => (
					<Paragraph key={key} color="slateBlue" mb={2}>
						{key}
					</Paragraph>
				))}
			</ConversationRevealSummaryRow>

			<ConversationRevealSummaryRow
				title={t('conversations.reveal.upload.fieldsSelected', {
					count: selectedFields.length,
				})}
				expandable={selectedFields.length > 0}
			>
				{selectedFields.map((field) => (
					<Paragraph key={field} color="slateBlue" mb={2}>
						{field}
					</Paragraph>
				))}
			</ConversationRevealSummaryRow>
		</Box>
	);
}
